/* eslint-disable import/prefer-default-export */
/**
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly prohibited.
 */

/**
 * This class represents the utility functions for distances.
 */
const MILE_TO_KILOMETER_CONVERSION = 1.609344;
const EARTH_RADIUS_MILES = 3958.76133381; // Exact radius used by turf.js
const FEET_TO_MILE_CONVERSION = 5280;

/**
 *
 * @param bps - value to convert
 * @param numberOfDecimals - default number of decimal places
 */
export const milesToKilometers = (miles: number, numberOfDecimals = 2): string => {
  const km = miles * MILE_TO_KILOMETER_CONVERSION;
  return km.toFixed(numberOfDecimals);
};

/**
 *
 * @param degrees - lat or long value in degrees
 * @returns value in radians
 */
const toRadians = (degrees: number) => {
  return degrees * (Math.PI / 180);
};

/**
 *
 * @param feet - distance in feet
 * @returns distance in miles
 */
export const feetToMiles = (feet: number) => {
  const miles = feet / FEET_TO_MILE_CONVERSION;
  return miles;
};

/**
 * Uses haversine formula to calculate distance between two lat long points given a constant altitude
 *
 * @param source - starting lat long
 * @param destination - ending lat long
 * @param altitudeFeet - altitude in feet
 * @returns distance between source and destination at altitudeFeet in miles
 */
export const distanceInMiles = (source: google.maps.LatLng, destination: google.maps.LatLng, altitudeFeet: number) => {
  const R = EARTH_RADIUS_MILES + feetToMiles(altitudeFeet); // Earth's radius plus airplane altitude in miles

  const lat1 = toRadians(source.lat());
  const lat2 = toRadians(destination.lat());
  const deltaLat = toRadians(destination.lat() - source.lat());
  const deltaLong = toRadians(destination.lng() - source.lng());

  const a =
    Math.sin(deltaLat / 2) * Math.sin(deltaLat / 2) +
    Math.cos(lat1) * Math.cos(lat2) * Math.sin(deltaLong / 2) * Math.sin(deltaLong / 2);

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));

  const distance = R * c; // Distance in miles
  return distance;
};
